import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { newProductsApi, NewProductsParams, NewProductCategory } from '../api/newProducts';
import ProductList from '../components/home/ProductList';
import Pagination from '../components/main/Pagination';
import PageHeader from '../components/main/PageHeader';
import SortFilterBar from '../components/main/SortFilterBar';
import SideMenu from '../components/main/SideMenu';
import type { Product } from '../types/product';
import { useBrands } from '../contexts/BrandContext';
import { enhanceProductsWithBrands } from '../utils/brandUtils';
import { getCategoryName } from '../utils/categoryUtils';
import i18n from '@/i18n';

export default function NewProducts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { brands } = useBrands();
  const { t } = useTranslation();

  const [rawProducts, setRawProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<NewProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'new' | 'popular' | 'price_low' | 'price_high'>('new');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = isMobile ? 12 : 20; // Responsive limit: 12 for mobile, 20 for PC
  
  // Enhance products with brand names using cached brand data
  const products = useMemo(() => {
    if (rawProducts.length > 0 && brands.length > 0) {
      return enhanceProductsWithBrands(rawProducts, brands);
    }
    return rawProducts;
  }, [rawProducts, brands]);

  const fetchNewProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: NewProductsParams = {
        page: currentPage,
        limit,
        sortBy,
        ...(selectedCategory !== null && { categoryId: selectedCategory }),
      };
      
      const response = await newProductsApi.getNewProducts(params);
      setRawProducts(response.products);
      setTotalPages(response.totalPages);
      setTotalItems(response.total);
    } catch (error) {
      console.error('Error fetching new products:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, sortBy, selectedCategory]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await newProductsApi.getNewProductCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching new product categories:', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    fetchNewProducts();
  }, [sortBy, currentPage, limit, selectedCategory, fetchNewProducts]);

  // Listen for side menu open event from mobile bottom navigation
  useEffect(() => {
    const handleOpenSideMenu = () => {
      setSideMenuOpen(true);
    };

    window.addEventListener('openSideMenu', handleOpenSideMenu);
    
    return () => {
      window.removeEventListener('openSideMenu', handleOpenSideMenu);
    };
  }, []);

  const handleSortChange = (newSortBy: 'new' | 'popular' | 'price_low' | 'price_high') => {
    setSortBy(newSortBy);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleCategoryClick = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset to first page when category changes
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'white',
        pb: 4,
      }}
    >
      {/* Header */}
      <PageHeader title={`${t('navigation.new')}`} />

      {/* Categories */}
      {categories.length > 0 && (
        <Container maxWidth="lg" sx={{ pt: { xs: 10, md: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              py: 2,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            <Chip
              label={t('common.all')}
              onClick={() => handleCategoryClick(null)}
              variant={selectedCategory === null ? 'filled' : 'outlined'}
              sx={{
                backgroundColor: selectedCategory === null ? theme.palette.custom.brown : 'transparent',
                color: selectedCategory === null ? 'white' : theme.palette.custom.brown,
                borderColor: theme.palette.custom.brown,
                '&:hover': {
                  backgroundColor: selectedCategory === null ? theme.palette.custom.brown : 'rgba(179, 144, 122, 0.1)',
                },
              }}
            />
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={getCategoryName(category, i18n.language)}
                onClick={() => handleCategoryClick(category.id)}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                sx={{
                  backgroundColor: selectedCategory === category.id ? theme.palette.custom.brown : 'transparent',
                  color: selectedCategory === category.id ? 'white' : theme.palette.custom.brown,
                  borderColor: theme.palette.custom.brown,
                  '&:hover': {
                    backgroundColor: selectedCategory === category.id ? theme.palette.custom.brown : 'rgba(179, 144, 122, 0.1)',
                  },
                }}
              />
            ))}
          </Box>
        </Container>
      )}

      {/* Products Count and Sort */}
      <SortFilterBar
        totalItems={totalItems}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />

      {/* Products List */}
      <ProductList
        products={products}
        loading={loading}
        variant="grid"
      />

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {/* Side Menu */}
      <SideMenu 
        open={sideMenuOpen} 
        onClose={() => setSideMenuOpen(false)} 
      />
    </Box>
  );
}
